import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import unifiedApi from '../../../utils/unifiedApi';

const describeResult = (result) => {
  const parts = [];
  if (result.regularSeasonChanged) {
    parts.push(`regular-season standings rebuilt for ${result.leaguesRebuilt} ${result.leaguesRebuilt === 1 ? 'league' : 'leagues'}`);
  }
  if (result.playoffChanged) {
    parts.push(`playoff standings recalculated for ${result.playoffLeaguesRecalculated} ${result.playoffLeaguesRecalculated === 1 ? 'league' : 'leagues'}`);
  }
  return parts.length ? `Scoring rules saved: ${parts.join(' and ')}.` : 'Scoring rules saved. Nothing changed.';
};

const ScoringTypeField = ({ id, label, value, options, onChange, disabled }) => {
  const selected = options.find((o) => o.value === value);
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <TextField
        id={id}
        select
        fullWidth
        label={label}
        value={selected ? value : ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.name}
          </MenuItem>
        ))}
      </TextField>
      {selected && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, wordBreak: 'break-word' }}>
          {selected.description}
        </Typography>
      )}
    </Box>
  );
};

/** Organization-wide scoring types used for every league's standings. Org Admins and Super Admins can change them. */
const ScoringRulesSettings = ({ canEdit = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState({ regularSeasonScoring: '', playoffScoring: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const applySettings = (data) => {
    setSettings(data);
    setDraft({ regularSeasonScoring: data.regularSeasonScoring, playoffScoring: data.playoffScoring });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      applySettings(await unifiedApi.getScoringRules());
    } catch {
      setMessage({ type: 'error', text: 'Failed to load scoring rules.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const changed = !!settings && (
    draft.regularSeasonScoring !== settings.regularSeasonScoring
    || draft.playoffScoring !== settings.playoffScoring
  );

  const save = async () => {
    setConfirmOpen(false);
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const result = await unifiedApi.updateScoringRules(draft);
      applySettings(result.settings);
      setMessage({ type: 'success', text: describeResult(result) });
    } catch (err) {
      setMessage({ type: 'error', text: err?.message || 'Failed to save scoring rules.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h6" gutterBottom>
        Scoring Rules
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        How games count towards standings in every league. Changing a rule recalculates standings for all
        leagues, including past seasons.
      </Typography>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={28} aria-label="Loading scoring rules" />
        </Box>
      )}

      {!loading && settings && (
        <>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 3, md: 2 }}>
            <ScoringTypeField
              id="regular-season-scoring"
              label="Regular season"
              value={draft.regularSeasonScoring}
              options={settings.regularSeasonOptions}
              onChange={(value) => setDraft((d) => ({ ...d, regularSeasonScoring: value }))}
              disabled={!canEdit || saving}
            />
            <ScoringTypeField
              id="playoff-scoring"
              label="Playoffs"
              value={draft.playoffScoring}
              options={settings.playoffOptions}
              onChange={(value) => setDraft((d) => ({ ...d, playoffScoring: value }))}
              disabled={!canEdit || saving}
            />
          </Stack>

          {canEdit && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 3, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => applySettings(settings)}
                disabled={!changed || saving}
                fullWidth={isMobile}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                onClick={() => setConfirmOpen(true)}
                disabled={!changed || saving}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                fullWidth={isMobile}
              >
                {saving ? 'Recalculating…' : 'Save scoring rules'}
              </Button>
            </Stack>
          )}
        </>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} fullScreen={isMobile} aria-labelledby="scoring-confirm-title">
        <DialogTitle id="scoring-confirm-title">Change scoring rules?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Standings for every league in your organization, including past seasons, will be recalculated with the
            new rules. Positions may change.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save}>
            Recalculate standings
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ScoringRulesSettings;
